import { localized, msg } from '@lit/localize';
import * as Sentry from '@sentry/browser';
import { html, LitElement, unsafeCSS } from 'lit';
import { customElement } from 'lit/decorators.js';
import componentColors from '../styles/component-colors.css?raw';
import {
  getErrorMessage,
  isResizeObserverLoopError,
} from '../utils/browser-errors';

/**
 * Error boundary component that catches errors and reports them to Sentry
 * Wrap your app components with this to catch rendering errors
 */
@customElement('sb-error-boundary')
@localized()
export class SbErrorBoundary extends LitElement {
  static styles = unsafeCSS(componentColors);

  private error: Error | null = null;
  private errorInfo: string | null = null;

  connectedCallback(): void {
    super.connectedCallback();
    // Listen for unhandled errors
    window.addEventListener('error', this.handleError);
    window.addEventListener('unhandledrejection', this.handleRejection);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener('error', this.handleError);
    window.removeEventListener('unhandledrejection', this.handleRejection);
  }

  private handleError = (event: ErrorEvent) => {
    if (
      isResizeObserverLoopError(event.error) ||
      isResizeObserverLoopError(event.message)
    ) {
      event.preventDefault();
      return;
    }

    this.error = event.error || new Error(event.message);
    this.errorInfo = event.message;
    Sentry.captureException(this.error, {
      contexts: {
        errorBoundary: {
          errorInfo: this.errorInfo,
        },
      },
    });
    this.requestUpdate();
  };

  private handleRejection = (event: PromiseRejectionEvent) => {
    if (isResizeObserverLoopError(event.reason)) {
      event.preventDefault();
      return;
    }

    const error =
      event.reason instanceof Error
        ? event.reason
        : new Error(String(event.reason));
    this.error = error;
    this.errorInfo = getErrorMessage(event.reason) || String(event.reason);
    Sentry.captureException(error, {
      contexts: {
        errorBoundary: {
          errorInfo: this.errorInfo,
          type: 'unhandledRejection',
        },
      },
    });
    this.requestUpdate();
  };

  render() {
    if (this.error) {
      return html`
        <div style="
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100vh;
          padding: 2rem;
          text-align: center;
        ">
          <h1 style="font-size: 2rem; margin-bottom: 1rem;">${msg('Something went wrong')}</h1>
          <p style="color: var(--spectrum-global-color-gray-700); margin-bottom: 2rem;">
            ${msg("We've been notified about this error and will look into it.")}
          </p>
          <button
            @click=${this.handleRetry}
            style="
              padding: 0.75rem 1.5rem;
              background: var(--spectrum-global-color-blue-500);
              color: white;
              border: none;
              border-radius: 4px;
              cursor: pointer;
              font-size: 1rem;
            "
          >
            ${msg('Try Again')}
          </button>
          ${
            import.meta.env.DEV
              ? html`
            <details style="margin-top: 2rem; text-align: left; max-width: 600px;">
              <summary style="cursor: pointer; margin-bottom: 0.5rem;">${msg('Error Details (Dev Only)')}</summary>
              <pre style="
                background: var(--spectrum-global-color-gray-100);
                padding: 1rem;
                border-radius: 4px;
                overflow: auto;
                font-size: 0.875rem;
              ">${this.error.stack || this.error.message}</pre>
            </details>
          `
              : ''
          }
        </div>
      `;
    }

    return html`<slot></slot>`;
  }

  private handleRetry = () => {
    this.error = null;
    this.errorInfo = null;
    window.location.reload();
  };
}
